/**
 * MemberProfile: the one member profile used everywhere.
 *
 *  - mode="self"  → the signed-in user's own record via api/members/me/* (/profile, HR my-profile)
 *  - mode="admin" → any record by MemberID via api/members/{id}/* (/admin/members/:memberId),
 *                   so visitors and children without a login work too
 *
 * Tabs: Personal, Contact, Addresses, Health, Family, Campus, Ministries and roles (read-only)
 * and Emergency contacts in both modes; Notes, Links, Activity and Admin in admin mode only. Host
 * pages can add their own tabs (HR adds Employment and Files) and header badges / actions.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconUser, IconAddressBook, IconMapPin, IconFirstAidKit, IconUsersGroup, IconBuildingChurch, IconHierarchy, IconPhoneCall,
  IconNotes, IconLink, IconHistory, IconShieldCheck, IconAlertCircle, IconRefresh, IconUserQuestion, IconTrash,
} from '@tabler/icons-react';
import { Stack, Tabs, Box, Alert, Skeleton, Button, Group, Card, EmptyState, Text, ScrollArea } from '@/components/ui';
import { TabHelp, useHelpActiveTab } from '@/components/help';
import { useMemberProfileApi } from './useMemberProfileApi';
import { useMemberProfilePermissions, type MemberProfileExtraTab, type MemberTabProps } from './types';
import { useMemberTypes } from './useMemberLookups';
import { MemberProfileHeader, type Completeness } from './MemberProfileHeader';
import { REQUIRED_PROFILE_FIELDS, errorMessage, errorStatus, type MemberRecord } from './memberFormat';
import { PersonalTab } from './tabs/PersonalTab';
import { ContactTab } from './tabs/ContactTab';
import { AddressesTab } from './tabs/AddressesTab';
import { HealthTab } from './tabs/HealthTab';
import { FamilyTab } from './tabs/FamilyTab';
import { CampusTab } from './tabs/CampusTab';
import { MinistriesTab } from './tabs/MinistriesTab';
import { EmergencyContactsTab } from './tabs/EmergencyContactsTab';
import { NotesTab } from './tabs/NotesTab';
import { LinksTab } from './tabs/LinksTab';
import { ActivityTab } from './tabs/ActivityTab';
import { AdminTab } from './tabs/AdminTab';

export interface MemberProfileProps {
  mode: 'self' | 'admin';
  /** Required in admin mode. */
  memberId?: number | null;
  /** Extra tabs from the host page (for example HR Employment and Files). */
  extraTabs?: MemberProfileExtraTab[];
  /** Extra badges in the header (for example HR employment status). */
  extraBadges?: ReactNode;
  /** Buttons shown at the top right of the header. */
  headerActions?: ReactNode;
  /** URL query parameter that holds the active tab ("tab" by default). Pass null to keep it in memory only. */
  tabParam?: string | null;
  /** Extra names accepted in the URL for a tab, e.g. { allergies: 'health' }. */
  tabAliases?: Record<string, string>;
  defaultTab?: string;
  /** Called whenever the member record loads or changes (null = none). */
  onMemberLoaded?: (member: MemberRecord | null) => void;
  /**
   * In-app help page key of the host page (for example "hr.my-profile" or "members.member"). When set,
   * the header, completeness meter, tab list and tabs carry tour anchors ("{key}.header",
   * "{key}.completeness", "{key}.tabs", "{key}.tab-{tab}") and the help framework is told which tab is
   * open, so the page's one "?" offers help for it. Leave it out (as /profile does) and nothing changes.
   */
  helpPageKey?: string;
}

interface TabDef { value: string; label: string; icon: ReactNode; render: () => ReactNode }

/** Tells the help framework which profile tab is open (rendered only when a help page key is set). */
function HelpActiveTab({ pageKey, tabKey }: { pageKey: string; tabKey: string | null }) {
  useHelpActiveTab(pageKey, tabKey);
  return null;
}

const RECOMMENDED = ['Home campus', 'Emergency contact', 'Address'] as const;

export function MemberProfile({
  mode, memberId, extraTabs = [], extraBadges, headerActions, tabParam = 'tab', tabAliases, defaultTab = 'personal', onMemberLoaded, helpPageKey,
}: MemberProfileProps) {
  const api = useMemberProfileApi(mode, memberId);
  const perms = useMemberProfilePermissions(mode);
  const { types } = useMemberTypes();
  const [searchParams, setSearchParams] = useSearchParams();
  const [localTab, setLocalTab] = useState<string>(defaultTab);

  const memberQuery = useQuery({
    queryKey: api.keys.member,
    queryFn: () => api.getMember(),
    enabled: mode === 'self' || !!memberId,
    retry: (count, e) => errorStatus(e) !== 404 && errorStatus(e) !== 403 && count < 2,
  });
  const member = memberQuery.data ?? null;

  useEffect(() => { onMemberLoaded?.(member); }, [member, onMemberLoaded]);

  // Recommended items for the completeness meter share their cache with the tabs.
  const hasMember = !!member;
  const addresses = useQuery({ queryKey: api.keys.addresses, queryFn: () => api.addresses.list(), enabled: hasMember });
  const emergency = useQuery({ queryKey: api.keys.emergency, queryFn: () => api.emergency.list(), enabled: hasMember });
  const locations = useQuery({ queryKey: api.keys.locations, queryFn: () => api.getLocations(), enabled: hasMember });

  const completeness = useMemo<Completeness | null>(() => {
    if (!member) return null;
    const missing = REQUIRED_PROFILE_FIELDS.filter((f) => f.isMissing(member)).map((f) => f.label);
    const extras: Record<(typeof RECOMMENDED)[number], boolean | null> = {
      'Home campus': locations.isSuccess ? (locations.data ?? []).length > 0 : null,
      'Emergency contact': emergency.isSuccess ? (emergency.data ?? []).length > 0 : null,
      Address: addresses.isSuccess ? (addresses.data ?? []).length > 0 : null,
    };
    let total = REQUIRED_PROFILE_FIELDS.length;
    let done = total - missing.length;
    for (const label of RECOMMENDED) {
      const v = extras[label];
      if (v === null) continue; // unknown (still loading or not readable): leave it out of the score
      total += 1;
      if (v) done += 1; else missing.push(label);
    }
    return { percent: total ? (done / total) * 100 : 100, missing };
  }, [member, addresses.isSuccess, addresses.data, emergency.isSuccess, emergency.data, locations.isSuccess, locations.data]);

  const tabs = useMemo<TabDef[]>(() => {
    if (!member) return [];
    const props: MemberTabProps = { api, member, perms };
    const list: TabDef[] = [
      { value: 'personal', label: 'Personal', icon: <IconUser size={16} />, render: () => <PersonalTab {...props} /> },
      { value: 'contact', label: 'Contact', icon: <IconAddressBook size={16} />, render: () => <ContactTab {...props} /> },
      { value: 'addresses', label: 'Addresses', icon: <IconMapPin size={16} />, render: () => <AddressesTab {...props} /> },
      { value: 'health', label: 'Health', icon: <IconFirstAidKit size={16} />, render: () => <HealthTab {...props} /> },
    ];
    if (perms.familyRead) list.push({ value: 'family', label: 'Family', icon: <IconUsersGroup size={16} />, render: () => <FamilyTab {...props} /> });
    list.push({ value: 'campus', label: 'Campus', icon: <IconBuildingChurch size={16} />, render: () => <CampusTab {...props} /> });
    if (perms.ministries || perms.departments || perms.designations) {
      list.push({ value: 'ministries', label: 'Ministries and roles', icon: <IconHierarchy size={16} />, render: () => <MinistriesTab {...props} /> });
    }
    list.push({ value: 'emergency', label: 'Emergency contacts', icon: <IconPhoneCall size={16} />, render: () => <EmergencyContactsTab {...props} /> });
    if (mode === 'admin') {
      if (perms.notes) list.push({ value: 'notes', label: 'Notes', icon: <IconNotes size={16} />, render: () => <NotesTab {...props} /> });
      list.push({ value: 'links', label: 'Links', icon: <IconLink size={16} />, render: () => <LinksTab {...props} /> });
      if (perms.audit) list.push({ value: 'activity', label: 'Activity', icon: <IconHistory size={16} />, render: () => <ActivityTab {...props} /> });
      list.push({ value: 'admin', label: 'Admin', icon: <IconShieldCheck size={16} />, render: () => <AdminTab {...props} /> });
    }
    return list;
  }, [api, member, perms, mode]);

  const allTabs = useMemo<TabDef[]>(() => {
    const extra = (first: boolean) => extraTabs.filter((t) => !!t.first === first)
      .map((t) => ({ value: t.value, label: t.label, icon: t.icon, render: () => t.content }));
    return [...extra(true), ...tabs, ...extra(false)];
  }, [extraTabs, tabs]);

  const resolveTab = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const key = raw.toLowerCase().trim();
    const target = tabAliases?.[key] ?? key;
    return allTabs.some((t) => t.value === target) ? target : null;
  };

  const urlTab = tabParam ? resolveTab(searchParams.get(tabParam)) : null;
  const fallbackTab = allTabs.some((t) => t.value === defaultTab) ? defaultTab : allTabs[0]?.value ?? null;
  const activeTab = tabParam ? urlTab ?? fallbackTab : resolveTab(localTab) ?? fallbackTab;

  const changeTab = (v: string | null) => {
    if (!v) return;
    if (!tabParam) { setLocalTab(v); return; }
    const next = new URLSearchParams(searchParams);
    if (v === fallbackTab) next.delete(tabParam); else next.set(tabParam, v);
    setSearchParams(next, { replace: true });
  };

  // ---------- states ----------
  if (memberQuery.isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={150} radius="md" />
        <Skeleton height={40} radius="md" />
        <Skeleton height={320} radius="md" />
      </Stack>
    );
  }

  if (memberQuery.isError) {
    const status = errorStatus(memberQuery.error);
    return (
      <Alert color={status === 404 ? 'yellow' : 'red'} icon={<IconAlertCircle size={16} />}
        title={status === 404 ? 'Member not found' : status === 403 ? 'No access' : 'Could not load the profile'}>
        <Stack gap="sm">
          <Text size="sm">
            {status === 404 ? 'There is no member with this ID.'
              : status === 403 ? 'You do not have permission to view this member.'
                : errorMessage(memberQuery.error)}
          </Text>
          {status !== 404 && status !== 403 && (
            <Group><Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => memberQuery.refetch()}>Try again</Button></Group>
          )}
        </Stack>
      </Alert>
    );
  }

  const tabsBlock = allTabs.length > 0 && activeTab && (
    <Tabs value={activeTab} onChange={changeTab} keepMounted={false}>
      {helpPageKey && <HelpActiveTab pageKey={helpPageKey} tabKey={activeTab} />}
      <ScrollArea type="auto" offsetScrollbars scrollbarSize={6}>
        <Tabs.List style={{ flexWrap: 'nowrap' }} data-help-id={helpPageKey ? `${helpPageKey}.tabs` : undefined}>
          {allTabs.map((t) => (
            <Tabs.Tab
              key={t.value}
              value={t.value}
              leftSection={t.icon}
              style={{ whiteSpace: 'nowrap' }}
              data-help-id={helpPageKey ? `${helpPageKey}.tab-${t.value}` : undefined}
            >
              {t.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </ScrollArea>
      <Box mt="md">
        {helpPageKey && <TabHelp pageKey={helpPageKey} tabKey={activeTab} />}
        {allTabs.map((t) => (
          <Tabs.Panel key={t.value} value={t.value}>{t.value === activeTab ? t.render() : null}</Tabs.Panel>
        ))}
      </Box>
    </Tabs>
  );

  if (!member) {
    // Self mode only: the login has no member row. Never claim one "will be created": PUT /me
    // needs an existing row.
    return (
      <Stack gap="md">
        <Card withBorder padding="lg" radius="md">
          <EmptyState
            icon={<IconUserQuestion size={36} />}
            title="No member record is linked to your login yet"
            description="Your member profile (personal details, family, campus and health) is linked by the church office. Please contact them and they will connect it to your login."
          />
        </Card>
        {tabsBlock}
      </Stack>
    );
  }

  const typeRow = types.find((t) => t.typeID === member.typeID);

  return (
    <Stack gap="md">
      {member.isDeleted && mode === 'admin' && (
        <Alert color="red" icon={<IconTrash size={16} />} title={member.mergedIntoMemberID ? 'This record was merged' : 'This record is deleted'}>
          {member.mergedIntoMemberID
            ? `It was merged into member #${member.mergedIntoMemberID} and is kept only for history.`
            : 'It is hidden from the directory and search. Restore it on the Admin tab.'}
        </Alert>
      )}
      <MemberProfileHeader
        member={member}
        mode={mode}
        typeName={member.typeName ?? typeRow?.name}
        typeColor={member.typeColor ?? typeRow?.color}
        completeness={completeness}
        actions={headerActions}
        extraBadges={extraBadges}
        helpPageKey={helpPageKey}
      />
      {tabsBlock}
    </Stack>
  );
}
